import { normalizeLocaleCode, setLocaleOverride, type Locale } from '../i18n'
import { applyUnitSystemFromLegacy } from '../state/unitSystemStore'
import { normalizeUnitSystem, type UnitSystem } from '../utils/units'

type PreferenceProfile = {
   preferredLocale: Locale | null
   unitSystem: UnitSystem
}

type PreferenceControllerDeps = {
   /** The mutable user profile holding the preferences. */
   userProfile: PreferenceProfile
   markStateChanged: () => void
   saveState: () => void
   syncState: (action: string) => Promise<unknown>
   /** Re-renders locale-dependent UI after a language change. */
   refreshLocaleDependentUi: () => void
   syncAccountStateToSolid: () => void
}

type LocaleOptions = {
   persist?: boolean
   sync?: boolean
   markChange?: boolean
   refresh?: boolean
}

type UnitSystemOptions = {
   persist?: boolean
   sync?: boolean
   markChange?: boolean
}

/**
 * Creates the controller that applies locale and unit-system preferences,
 * optionally persisting and syncing them.
 *
 * @param deps - The profile, persistence, and refresh callbacks.
 * @returns The preference actions.
 */
export function createPreferenceController(deps: PreferenceControllerDeps) {
   /**
    * Switches the active locale, re-rendering now and again once the locale
    * bundle has loaded.
    *
    * @param locale - The locale to load, or null for the browser default.
    * @param refresh - Whether to re-render locale-dependent UI.
    */
   function loadLocale(locale: Locale | null, refresh: boolean) {
      const loadPromise = setLocaleOverride(locale)
      if (!refresh) {
         return
      }
      deps.refreshLocaleDependentUi()
      loadPromise
         .then(() => deps.refreshLocaleDependentUi())
         .catch((error: unknown) => {
            console.error('Failed to update locale-dependent UI', error)
         })
   }

   /**
    * Persists and syncs a changed preference according to the options.
    *
    * @param action - The sync action name.
    * @param opts - Whether to mark changed, persist, and sync.
    */
   function commitPreference(action: string, opts: Required<UnitSystemOptions>) {
      if (opts.markChange) {
         deps.markStateChanged()
      }
      if (opts.persist) {
         deps.saveState()
      }
      if (opts.sync) {
         deps.syncState(action).catch(error => {
            console.error(`Failed to sync ${action}`, error)
         })
      }
   }

   /**
    * Applies a locale preference, optionally persisting, syncing, and refreshing the UI.
    *
    * @param localeCode - The locale code to apply.
    * @param options - Whether to persist, sync, mark changed, and refresh.
    */
   function applyLocalePreference(localeCode: unknown, options: LocaleOptions = {}) {
      const opts = {
         persist: true,
         sync: true,
         markChange: true,
         refresh: true,
         ...options,
      }
      const normalized =
         typeof localeCode === 'string' ? normalizeLocaleCode(localeCode) : null
      const currentPreferred =
         typeof deps.userProfile.preferredLocale === 'string' ?
            normalizeLocaleCode(deps.userProfile.preferredLocale)
         :  null
      if (normalized === currentPreferred) {
         loadLocale(normalized, opts.refresh)
         return
      }
      deps.userProfile.preferredLocale = normalized
      loadLocale(normalized, opts.refresh)
      commitPreference('locale-change', opts)
   }

   /**
    * Applies a unit-system preference, persisting and syncing it like the locale.
    *
    * @param value - The candidate unit system.
    * @param options - Whether to persist, sync, and mark the change.
    */
   function applyUnitSystemPreference(value: unknown, options: UnitSystemOptions = {}) {
      const opts = { persist: true, sync: true, markChange: true, ...options }
      const normalized = normalizeUnitSystem(value)
      const changed = normalized !== deps.userProfile.unitSystem
      deps.userProfile.unitSystem = normalized
      applyUnitSystemFromLegacy(normalized)
      deps.syncAccountStateToSolid()
      if (changed) {
         commitPreference('unit-system-change', opts)
      }
   }

   return {
      applyLocalePreference,
      applyUnitSystemPreference,
      /**
       * Applies the stored locale preference on startup without persisting or syncing.
       */
      initializeLocalePreference: () =>
         applyLocalePreference(deps.userProfile.preferredLocale, {
            persist: false,
            sync: false,
            markChange: false,
            refresh: false,
         }),
   }
}
